import { useState } from "react";
import { EllipsisVertical, File, Plus, Trash, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DataTable, Filters, PageHeader, Pagination, showSuccessToast, showErrorToast, type Column } from "@/components/common";
import { ConfirmDialog } from "@/components/dialogs/ConfirmDialog";
import { CompanyForm } from "@/components/forms/CompanyForm";
import { Avatar, AvatarImage, AvatarFallback, Badge, Button, Card, CardContent, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger, } from "@/components/ui";
import { useCompany } from "@/hooks/useCompany";
import type { CompanyProfile } from "@/types/company";
import { logError } from "@/utils/logger";

// Helper function to get initials
const getInitials = (name: string) => {
    const names = name.split(' ');
    return names.length > 1
        ? `${names[0][0]}${names[1][0]}`
        : name.substring(0, 2);
};

const Companies = () => {
    const { t } = useTranslation();
    const { companies, deleteCompany, addCompany, updateCompany } = useCompany();
    const [searchQuery, setSearchQuery] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [selectedCompanies, setSelectedCompanies] = useState<Set<string>>(new Set());
    const [subscriptionFilter, setSubscriptionFilter] = useState("all");
    const [addCompanyDialogOpen, setAddCompanyDialogOpen] = useState(false);
    const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
    const [deleteCompanyDialogOpen, setDeleteCompanyDialogOpen] = useState(false);
    const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
    const [companyToEdit, setCompanyToEdit] = useState<CompanyProfile | null>(null);
    const [companyToDelete, setCompanyToDelete] = useState<CompanyProfile | null>(null);
    const [newCompany] = useState<Partial<CompanyProfile>>({
        companyName: "",
        psNumber: "",
        glnNumber: "",
        generalEmail: "",
        productEmail: "",
        website: "",
        phone: "",
        description: "",
        logo: "",
        address: {
            street: "",
            postalCode: "",
            city: "",
            country: "",
        },
        subscription: {
            type: "basic",
            startDate: new Date().toISOString().split('T')[0],
            extraServices: "",
        },
    });
    const [viewMode, setViewMode] = useState(false);
    const [sortConfig, setSortConfig] = useState<{ key: keyof CompanyProfile | null; direction: 'asc' | 'desc' }>({
        key: null,
        direction: 'asc'
    });

    // Filter catalogs based on search query and filters
    const filteredCompanies = companies.filter(company => {
        const matchesSearch = company.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
            company.psNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            company.glnNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
            company.generalEmail.toLowerCase().includes(searchQuery.toLowerCase());

        const matchesSubscription = subscriptionFilter === "all" ||
            company.subscription.type.toLowerCase().includes(subscriptionFilter.toLowerCase());

        return matchesSearch && matchesSubscription;
    }).sort((a, b) => {
        if (!sortConfig.key) return 0;

        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];

        if (typeof aValue === 'string' && typeof bValue === 'string') {
            return sortConfig.direction === 'asc'
                ? aValue.localeCompare(bValue)
                : bValue.localeCompare(aValue);
        }

        if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'asc'
                ? aValue - bValue
                : bValue - aValue;
        }

        return 0;
    })

    // Calculate pagination
    const totalPages = Math.ceil(filteredCompanies.length / pageSize);
    const startIndex = (currentPage - 1) * pageSize;
    const paginatedCompanies = filteredCompanies.slice(startIndex, startIndex + pageSize);

    // Columns for the table
    const columns: Column<CompanyProfile>[] = [
        {
            key: "companyName",
            label: t("company.profile.companyName"),
            sortable: true,
            render: (_, company) => (
                <div className="flex items-center gap-3">
                    <Avatar>
                        <AvatarImage src={company.logo} alt={company.companyName} />
                        <AvatarFallback>{getInitials(company.companyName)}</AvatarFallback>
                    </Avatar>
                    <div>
                        <p className="font-medium">{company.companyName}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{company.generalEmail}</p>
                    </div>
                </div>
            ),
        },
        {
            key: "psNumber",
            label: t("company.profile.psNumber"),
            sortable: true,
            render: (value) => String(value),
        },
        {
            key: "glnNumber",
            label: t("company.profile.glnNumber"),
            sortable: true,
            render: (value) => String(value),
        },
        {
            key: "subscription",
            label: t("company.subscription"),
            sortable: true,
            render: (value) => (
                <Badge variant="outline" className="capitalize">
                    {(value as CompanyProfile['subscription']).type}
                </Badge>
            ),
        },
        {
            key: "actions",
            label: "",
            render: (_, company) => (
                <div className="text-right mr-2">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                                <EllipsisVertical className="h-4 w-4" strokeWidth={1.5} />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                            <DropdownMenuLabel>{t("common.actions")}</DropdownMenuLabel>
                            <DropdownMenuItem
                                className="flex items-center cursor-pointer"
                                onClick={() => handleViewCompany(company)}
                            >
                                <File className="mr-2 h-4 w-4" strokeWidth={1.5} />
                                <span>{t("action.view")}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="flex items-center cursor-pointer"
                                onClick={() => handleEditCompanyClick(company)}
                            >
                                <Pencil className="mr-2 h-4 w-4" strokeWidth={1.5} />
                                <span>{t("action.edit")}</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                className="text-red-600 cursor-pointer focus:text-red-600"
                                onClick={() => handleDeleteCompanyClick(company)}
                            >
                                <Trash className="mr-2 h-4 w-4" strokeWidth={1.5} />
                                <span>{t("action.delete")}</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>
            ),
        },
    ];

    // Filters voor de filter component
    const filters = [
        {
            name: "subscription",
            value: subscriptionFilter,
            options: [
                { value: "basic", label: t("company.subscriptions.basic") },
                { value: "premium", label: t("company.subscriptions.premium") },
                { value: "premiumPlus", label: t("company.subscriptions.premiumPlus") },
            ],
            onChange: setSubscriptionFilter,
        },
    ];

    // Handlers for company actions
    const handleViewCompany = (company: CompanyProfile) => {
        setCompanyToEdit(company);
        setViewMode(true);
        setEditCompanyDialogOpen(true);
    };

    const handleAddCompany = async (company: Partial<CompanyProfile>) => {
        try {
            await addCompany(company as Omit<CompanyProfile, "id">);
            setAddCompanyDialogOpen(false);
            showSuccessToast(t("success.company.addSuccess"));
        } catch (error) {
            logError('Error adding company:', error as Error);
            showErrorToast(t("error.company.addError"));
        }
    };

    const handleEditCompanyClick = (company: CompanyProfile) => {
        setCompanyToEdit(company);
        setViewMode(false);
        setEditCompanyDialogOpen(true);
    };

    const handleEditCompany = async (data: Partial<CompanyProfile>) => {
        if (!companyToEdit) return;

        try {
            await updateCompany({ ...companyToEdit, ...data });
            setEditCompanyDialogOpen(false);
            setCompanyToEdit(null);
            setViewMode(false);
            showSuccessToast(t("success.company.updateSuccess"));
        } catch (error) {
            logError('Error updating company:', error as Error);
            showErrorToast(t("error.company.updateError"));
        }
    };

    const handleDeleteCompanyClick = (company: CompanyProfile) => {
        setCompanyToDelete(company);
        setDeleteCompanyDialogOpen(true);
    };

    const handleDeleteCompany = async () => {
        if (!companyToDelete) return;

        try {
            await deleteCompany(companyToDelete.psNumber);
            setDeleteCompanyDialogOpen(false);
            setCompanyToDelete(null);
            showSuccessToast(t("success.company.deleteSuccess", { name: companyToDelete.companyName }));
        } catch (error) {
            logError('Error deleting company:', error as Error);
            showErrorToast(t("error.company.deleteError", { name: companyToDelete.companyName }));
        }
    };

    const handleSelectAll = () => {
        if (selectedCompanies.size === paginatedCompanies.length) {
            setSelectedCompanies(new Set());
        } else {
            setSelectedCompanies(new Set(paginatedCompanies.map(company => company.psNumber)));
        }
    };

    const handleSelectItem = (company: CompanyProfile) => {
        const newSelected = new Set(selectedCompanies);
        if (newSelected.has(company.psNumber)) {
            newSelected.delete(company.psNumber);
        } else {
            newSelected.add(company.psNumber);
        }
        setSelectedCompanies(newSelected);
    };

    const handleBulkDelete = async () => {
        try {
            await Promise.all(
                Array.from(selectedCompanies).map((id) => deleteCompany(id))
            );
            setSelectedCompanies(new Set());
            setBulkDeleteDialogOpen(false);
            showSuccessToast(t("success.company.bulkDeleteSuccess", { count: selectedCompanies.size }));
        } catch (error) {
            logError('Error bulk deleting companies:', error as Error);
            showErrorToast(t("error.company.bulkDeleteError", { count: selectedCompanies.size }));
        }
    };

    return (
        <div className="flex flex-col gap-6">
            <PageHeader
                title={t("company.title")}
                description={t("company.description")}
                action={{
                    label: t("company.add"),
                    icon: Plus,
                    onClick: () => setAddCompanyDialogOpen(true)
                }}
            />

            <Filters
                searchQuery={searchQuery}
                onSearchChange={setSearchQuery}
                filters={filters}
                selectedCount={selectedCompanies.size}
                onBulkDelete={selectedCompanies.size > 0 ? () => setBulkDeleteDialogOpen(true) : undefined}
            />

            <Card className="p-0">
                <CardContent className="p-0">
                    <DataTable<CompanyProfile>
                        columns={columns}
                        data={paginatedCompanies}
                        selectedItems={selectedCompanies}
                        onSelectAll={handleSelectAll}
                        onSelectItem={handleSelectItem}
                        getId={(company) => company.psNumber}
                        sortConfig={sortConfig}
                        onSort={(key) => {
                            setSortConfig(prev => ({
                                key: key as keyof CompanyProfile,
                                direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
                            }));
                        }}
                        className="p-0 w-full [&_tr_td:first-child]:pl-6 [&_tr_td:last-child]:pr-6 [&_tr_th:first-child]:!pl-6 [&_tr_th:last-child]:!pr-6 [&_tr_td]:py-3 [&_tr_th]:py-5"
                    />

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        pageSize={pageSize}
                        onPageChange={setCurrentPage}
                        onPageSizeChange={setPageSize}
                    />
                </CardContent>
            </Card>

            {addCompanyDialogOpen && (
                <CompanyForm
                    company={newCompany}
                    onSubmit={handleAddCompany}
                    onCancel={() => setAddCompanyDialogOpen(false)}
                    mode="edit"
                />
            )}

            {editCompanyDialogOpen && companyToEdit && (
                <CompanyForm
                    company={companyToEdit}
                    onSubmit={handleEditCompany}
                    onCancel={() => {
                        setEditCompanyDialogOpen(false);
                        setCompanyToEdit(null);
                        setViewMode(false);
                    }}
                    mode={viewMode ? "view" : "edit"}
                />
            )}

            <ConfirmDialog
                open={deleteCompanyDialogOpen}
                onOpenChange={setDeleteCompanyDialogOpen}
                title={t("company.delete")}
                description={t("company.deleteDescription")}
                onConfirm={handleDeleteCompany}
            />

            <ConfirmDialog
                open={bulkDeleteDialogOpen}
                onOpenChange={setBulkDeleteDialogOpen}
                title={t("company.bulkDelete")}
                description={t("company.bulkDeleteDescription", { count: selectedCompanies.size })}
                onConfirm={handleBulkDelete}
            />
        </div>
    );
};

export default Companies;